import { useState, useRef, useMemo } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { DataGrid } from "@mui/x-data-grid";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  genegeneinteractionsColumns,
  genepathwayassociationsColumns,
  genephenotypeassociationsColumns,
} from "../../utils/tableheaders";
import Barchart from "../Barchart/Barchart";
import { useTheme } from "../../theme/useTheme";
import { DataGridToolbar } from "../DataGridToolbar";
import { firstUpperCase } from "../../utils/utils";
import { GeneTherapeuticHeader } from "../Header/GeneTherapeuticHeader";
import HeatmapWithLegend from "../Heatmap/HeatmapWithLegend";

const KnowledgeGraphAssociations = (props: any) => {
  const {
    tabdata,
    radioval,
    setTabdata,
    setRadioval,
    setLoading,
    loading,
    mode,
  } = useTheme();
  const [desc, setDesc] = useState(false);

  /*     GRID STRTS       */
  const [fullscreen, setFullscreen] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const apiRef = useRef<any>(null);
  //  Table data
  const paginationModel = { page: 0, pageSize: 10 };
  // Filter rows by searchValue (for DataGrid search)
  const filteredRows = useMemo(() => {
    if (!searchValue.trim()) return tabdata.genetbl.data.rows;
    const val = searchValue.trim().toLowerCase();
    return tabdata.genetbl.data.rows.filter((row: any) => {
      // Try to match any string value in the row
      return Object.values(row)
        .filter((v) => typeof v === "string")
        .some((v) => v.toLowerCase().includes(val));
    });
  }, [tabdata.genetbl.data.rows, searchValue]);
  /*     GRID ENDS         */

  const changeRadioval = async (type: string) => {
    setLoading(true);

    // reset table data
    setTabdata({
      ...tabdata,
      genetbl: {
        data: {
          rows: [],
          headers: [],
        },
        message: "",
      },
    });

    if (type === "genegeneinteractions") {
      setRadioval("genegeneinteractions");
      const response = await axios.get(
        `${API_BASE_URL}/api/tess/gene-explorer/gene-explorer/gene-interactions?gene_id=${props.selectedgene
        }&indication=${firstUpperCase(props.therapeuticAreaInternal)}`,
      );
      setTabdata({
        ...tabdata,
        genetbl: {
          data: {
            rows: response.data.data.map((itm: any, i: number) => ({
              ...itm,
              id: i + 1,
            })),
            headers: genegeneinteractionsColumns,
          },
          message: response.data.message,
        },
      });
      setLoading(false);
    }
    if (type === "genepathwayassociations") {
      setRadioval("genepathwayassociations");
      const response = await axios.get(
        `${API_BASE_URL}/api/tess/gene-explorer/gene-explorer/pathway-associations?gene_id=${
          props.selectedgene
        }&indication=${firstUpperCase(props.therapeuticAreaInternal)}`,
      );
      setTabdata({
        ...tabdata,
        genetbl: {
          data: {
            rows: response.data.data.map((itm: any, i: number) => ({
              ...itm,
              id: i + 1,
            })),
            headers: genepathwayassociationsColumns,
          },
          message: response.data.message,
        },
      });
      setLoading(false);
    }
    if (type === "genephenotypeassociations") {
      setRadioval("genephenotypeassociations");
      const response = await axios.get(
        `${API_BASE_URL}/api/tess/gene-explorer/gene-explorer/phenotype-associations?gene_id=${props.selectedgene
        }&indication=${firstUpperCase(props.therapeuticAreaInternal)}`,
      );
      setTabdata({
        ...tabdata,
        genetbl: {
          data: {
            rows: response.data.data.map((itm: any, i: number) => ({
              ...itm,
              id: i + 1,
            })),
            headers: genephenotypeassociationsColumns,
          },
          message: response.data.message,
        },
      });
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <GeneTherapeuticHeader
        selectedGene={props.selectedgene}
        therapeuticArea={props.therapeuticArea ?? undefined}
        taxonomyNodes={props.taxonomyNodes}
        selectedDiseaseIds={props.selectedDiseaseIds}
        onDiseaseSelectionChange={props.onDiseaseSelectionChange}
      />
      <div className="">
        {props.selectedgene ? (
          <div className="space-y-4">
            {tabdata?.geneinfo?.data?.length > 0 ? (
              <>
                {/*   VERTICAL ACCORDIAN        */}
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <div
                    className="flex items-center justify-between px-4 py-3 rounded-t-xl cursor-pointer"
                    onClick={() => setDesc(!desc)}
                  >
                    <span className="text-base font-semibold text-gray-900 dark:text-white truncate">
                      {tabdata?.geneinfo?.data?.length > 0
                        ? tabdata?.geneinfo?.data[0]
                        : props.nodata.title}
                    </span>
                    <span className="ml-2">
                      {desc ? (
                        <ChevronUpIcon className="h-6 w-6 text-gray-500 dark:text-gray-300" />
                      ) : (
                        <ChevronDownIcon className="h-6 w-6 text-gray-500 dark:text-gray-300" />
                      )}
                    </span>
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-200 ${desc
                      ? "max-h-[300px] opacity-100 py-4 px-4"
                      : "max-h-0 opacity-0 py-0 px-4"
                      }`}
                    aria-expanded={desc}
                  >
                    <p className="text-gray-700 dark:text-gray-200 text-base">
                      {tabdata.geneinfo?.data[1]}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-5">
                  {/*   Disease Score Panel   */}
                  <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md py-4 px-4 flex flex-col dark:border-gray-600">
                    <h4 className="text-md font-bold mb-2 text-gray-900 dark:text-gray-100">
                      Gene disease association score
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300">
                      Gene-disease association strength is measured as a
                      z-score, which quantifies the statistical strength of the
                      association. Higher z-scores indicate stronger
                      associations. The dashed yellow line marks the medium
                      score threshold, while the dashed red line indicates the
                      high score threshold. A gene is considered highly
                      associated with a disease if its z-score exceeds the red
                      line.
                    </p>
                    {/*   BAR CHART              */}

                    {!tabdata.diseaseassociationscores?.data?.zscore.every(
                      (value: number) => value === 0,
                    ) ? (
                      <div className="w-full py-2 h-[300px] mt-4">
                        <Barchart
                          title="Gene disease association score"
                          bgcolor="#1565c0"
                          yaxislabel="z-score"
                          xaxislabel="Disease"
                          yaxis={tabdata.diseaseassociationscores?.data?.zscore}
                          xaxis={
                            tabdata.diseaseassociationscores?.data?.disease
                          }
                        />
                      </div>
                    ) : (
                      <div className="w-full py-2 h-auto mt-4">
                        <div className="text-center py-8 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                          <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                            No disease association scores found for{" "}
                            {props.selectedgene}.
                          </p>
                        </div>
                      </div>
                    )}
                    {/*   DIVIDER                */}
                    <div className="border-t border-gray-300 dark:border-gray-600 my-6"></div>
                    <h4 className="text-md font-bold mb-2 text-gray-900 dark:text-gray-100">
                      Known gene-disease associations
                    </h4>
                    <p className="pt-4 text-gray-700 dark:text-gray-300">
                      Heatmap indicates which gene-disease associations are
                      known, i.e., with an existing link in the knowledge graph
                      (KG).
                    </p>
                    {/*   HEAT MAP               */}

                    {!tabdata.diseaseassociationsknown?.data?.zval?.every(
                      (row: any) =>
                        Array.isArray(row)
                          ? row.every((value: number) => value === 0)
                          : row === 0,
                    ) ? (
                      <div className="w-full py-8 mt-8">
                        <HeatmapWithLegend
                          title="Known gene-disease associations"
                          yaxisvalues={[0, 1]}
                          xaxisvalues={
                            tabdata.diseaseassociationsknown?.data?.xval
                          }
                          zaxisvalues={
                            tabdata.diseaseassociationsknown?.data?.zval
                          }
                        />
                      </div>
                    ) : (
                      <div className="w-full py-2 h-auto mt-4">
                        <div className="text-center py-8 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                          <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                            No known gene-disease associations found for{" "}
                            {props.selectedgene}.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  {/*   Association Table Panel   */}
                  <div className="bg-white dark:bg-gray-900 rounded-xl shadow-md py-4 px-4 flex flex-col dark:border-gray-600">
                    <h4 className="text-md font-bold mb-2 text-gray-900 dark:text-gray-100">
                      Select gene association type to get scores
                    </h4>
                    {/*   HORIZONTAL RADIO BUTTON LIST   */}
                    <ul className="items-center w-full text-sm font-medium text-gray-900 dark:text-gray-100 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg sm:flex mb-2">
                      <li className="w-full border-b border-gray-300 dark:border-slate-600 sm:border-b-0 sm:border-r">
                        <div className="flex items-center ps-3">
                          <input
                            id="radio-pathway"
                            type="radio"
                            value="genepathwayassociations"
                            name="association-type"
                            checked={radioval === "genepathwayassociations"}
                            onChange={() => changeRadioval("genepathwayassociations")}
                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-slate-600 border-gray-300 dark:border-slate-500 focus:outline-none cursor-pointer"
                          />
                          <label
                            htmlFor="radio-pathway"
                            className="w-full py-3 select-none ms-2 text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer"
                          >
                            Gene-Pathway
                          </label>
                        </div>
                      </li>
                      <li className="w-full border-b border-gray-300 dark:border-slate-600 sm:border-b-0 sm:border-r">
                        <div className="flex items-center ps-3">
                          <input
                            id="radio-gene"
                            type="radio"
                            value="genegeneinteractions"
                            name="association-type"
                            checked={radioval === "genegeneinteractions"}
                            onChange={() => changeRadioval("genegeneinteractions")}
                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-slate-600 border-gray-300 dark:border-slate-500 focus:outline-none cursor-pointer"
                          />
                          <label
                            htmlFor="radio-gene"
                            className="w-full py-3 select-none ms-2 text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer"
                          >
                            Gene-Gene
                          </label>
                        </div>
                      </li>
                      <li className="w-full">
                        <div className="flex items-center ps-3">
                          <input
                            id="radio-phenotype"
                            type="radio"
                            value="genephenotypeassociations"
                            name="association-type"
                            checked={radioval === "genephenotypeassociations"}
                            onChange={() => changeRadioval("genephenotypeassociations")}
                            className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-slate-600 border-gray-300 dark:border-slate-500 focus:outline-none cursor-pointer"
                          />
                          <label
                            htmlFor="radio-phenotype"
                            className="w-full py-3 select-none ms-2 text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer"
                          >
                            Gene-Phenotype
                          </label>
                        </div>
                      </li>
                    </ul>
                    <div className="py-2">
                      {radioval === "genephenotypeassociations" && (
                        <p className="text-gray-700 dark:text-gray-300">
                          Gene-phenotype associations sorted by ML-based link
                          prediction score between the chosen gene and
                          phenotypes. Heart disease-related phenotypes are
                          highlighted in teal.The 'in_KG' flag indicates whether
                          this association is known, i.e., with an existing link
                          in the knowledge graph (KG).
                        </p>
                      )}
                      {radioval === "genepathwayassociations" && (
                        <p className="text-gray-700 dark:text-gray-300">
                          Gene-pathway associations sorted by ML-based link
                          prediction score between the chosen gene and gene
                          ontology (GO). The 'in_KG' flag indicates whether this
                          association is known, i.e., with an existing link in
                          the knowledge graph (KG).
                        </p>
                      )}
                      {radioval === "genegeneinteractions" && (
                        <p className="text-gray-700 dark:text-gray-300">
                          Gene-gene interactions sorted by ML-based link
                          prediction score between the chosen gene and other
                          genes. The 'in_KG' flag indicates whether this
                          interaction is known, i.e., with an existing link in
                          the knowledge graph (KG). Gene-gene interactions in
                          the KG were mined from Hubble.Top Heart
                          disease-associated genes are highlighted in teal.
                        </p>
                      )}
                    </div>
                    {/*   TABLE STARTS             */}
                    <div className="pt-4">
                      <div
                        style={{
                          width: fullscreen ? "100vw" : "100%",
                          height: fullscreen ? "100vh" : "100%",
                          position: fullscreen ? "fixed" : "relative",
                          top: fullscreen ? 0 : undefined,
                          left: fullscreen ? 0 : undefined,
                          zIndex: fullscreen ? 1300 : undefined,
                          background: fullscreen
                            ? mode === "dark"
                              ? "#1f2937"
                              : "#fff"
                            : undefined,
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                        }}
                      >
                        <DataGrid
                          apiRef={apiRef}
                          rows={filteredRows}
                          columns={tabdata.genetbl.data.headers}
                          rowCount={filteredRows.length}
                          paginationMode="client"
                          getRowClassName={(params) => {
                            return params.row.Heart_related === true ||
                              params.row.Heart_related === "YES"
                              ? "heart-related-row"
                              : "";
                          }}
                          sx={{
                            border: 0,
                            "& .MuiDataGrid-root": {
                              backgroundColor:
                                mode === "dark" ? "#1f2937" : "#fff",
                              color: mode === "dark" ? "#e5e7eb" : "#111827",
                            },
                            "& .MuiDataGrid-overlay": {
                              backgroundColor:
                                mode === "dark" ? "#1f2937" : "#fff",
                              color: mode === "dark" ? "#e5e7eb" : "#6b7280",
                            },
                            "& .MuiDataGrid-cell": {
                              borderColor:
                                mode === "dark" ? "#374151" : "#e0e0e0",
                              color: mode === "dark" ? "#e5e7eb" : "#111827",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                              backgroundColor:
                                mode === "dark" ? "#374151" : "#f9fafb",
                              borderColor:
                                mode === "dark" ? "#4b5563" : "#bdbdbd",
                              color: mode === "dark" ? "#f3f4f6" : "#111827",
                            },
                            "& .MuiDataGrid-columnHeader": {
                              backgroundColor:
                                mode === "dark" ? "#374151" : "#f9fafb",
                              color: mode === "dark" ? "#f3f4f6" : "#111827",
                              "&:focus": {
                                outline: "none",
                              },
                              "&:focus-within": {
                                outline: "none",
                              },
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                              fontWeight: 600,
                              color: mode === "dark" ? "#f3f4f6" : "#111827",
                            },
                            "& .MuiDataGrid-columnSeparator": {
                              color: mode === "dark" ? "#4b5563" : "#e0e0e0",
                            },
                            "& .MuiDataGrid-columnHeaderTitleContainer": {
                              color: mode === "dark" ? "#f3f4f6" : "#111827",
                            },
                            "& .MuiDataGrid-row": {
                              backgroundColor:
                                mode === "dark" ? "#1f2937" : "#fff",
                              "&:nth-of-type(odd)": {
                                backgroundColor:
                                  mode === "dark" ? "#1f2937" : "#f9fafb",
                              },
                              "&:hover": {
                                backgroundColor:
                                  mode === "dark" ? "#374151" : "#f3f4f6",
                              },
                            },
                            "& .heart-related-row": {
                              backgroundColor:
                                mode === "dark" ? "#0d4139" : "#e6f7f5",
                              "&:nth-of-type(odd)": {
                                backgroundColor:
                                  mode === "dark" ? "#0d4139" : "#e6f7f5",
                              },
                              "&:hover": {
                                backgroundColor:
                                  mode === "dark" ? "#134e47" : "#d1f5f0",
                              },
                            },
                            "& .MuiDataGrid-footerContainer": {
                              backgroundColor:
                                mode === "dark" ? "#1f2937" : "#fff",
                              borderColor:
                                mode === "dark" ? "#374151" : "#e0e0e0",
                            },
                            "& .MuiTablePagination-root": {
                              color: mode === "dark" ? "#e5e7eb" : "#111827",
                            },
                            "& .MuiTablePagination-selectIcon": {
                              color: mode === "dark" ? "#9ca3af" : "#111827",
                            },
                            "& .MuiTablePagination-select": {
                              color: mode === "dark" ? "#e5e7eb" : "#111827",
                            },
                            "& .MuiTablePagination-displayedRows": {
                              color: mode === "dark" ? "#e5e7eb" : "#111827",
                            },
                            "& .MuiTablePagination-actions button": {
                              color: mode === "dark" ? "#e5e7eb" : "#111827",
                              "&.Mui-disabled": {
                                color: mode === "dark" ? "#4b5563" : "#9ca3af",
                                cursor: "not-allowed",
                                opacity: 0.5,
                              },
                            },
                            "& .MuiDataGrid-iconButtonContainer": {
                              "& button": {
                                color: mode === "dark" ? "#9ca3af" : "#111827",
                              },
                            },
                            "& .MuiDataGrid-menuIconButton": {
                              color: mode === "dark" ? "#9ca3af" : "#111827",
                            },
                            "& .MuiDataGrid-sortIcon": {
                              color: mode === "dark" ? "#9ca3af" : "#111827",
                            },
                          }}
                          pageSizeOptions={[10, 20]}
                          getRowId={(row: any) => row.id}
                          initialState={{ pagination: { paginationModel } }}
                          disableRowSelectionOnClick={true}
                          hideFooter={filteredRows.length === 0}
                          localeText={{
                            noRowsLabel: "No association data available",
                          }}
                          slots={{
                            toolbar: (props: any) => (
                              <DataGridToolbar
                                apiRef={apiRef}
                                fullscreen={fullscreen}
                                setFullscreen={setFullscreen}
                                searchActive={searchActive}
                                setSearchActive={setSearchActive}
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                                {...props}
                              />
                            ),
                          }}
                        />
                      </div>
                    </div>
                    {/*   TABLE ENDS               */}
                  </div>
                </div>
              </>
            ) : (
              !loading && (
                <div className="text-center py-8 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm font-medium text-red-600 dark:text-red-400">
                    No knowledge Graph Association Data found for{" "}
                    {props.selectedgene}. Try another disease name, check
                    spelling, or broaden your search.
                  </p>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <div className="text-6xl mb-4">🧬</div>
            <p className="text-gray-600 dark:text-gray-400">
              Enter a gene symbol to view detailed information
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeGraphAssociations;
